import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, Typography, Layout, Row, Col } from "antd";
import { LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { ShowToast, Severty } from "../helper/toast";

const { Paragraph, Title, Text } = Typography;
const { Content } = Layout;

const ResetPasswordFromLink = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useRequest();

  const params = new URLSearchParams(location.search);
  const email = params.get("email") || params.get("emailAddress") || "";
  const otp = params.get("otp") || "";

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const emailHint = useMemo(() => {
    if (!email) return "";
    const at = email.indexOf("@");
    if (at <= 1) return email;
    return `${email[0]}***${email.slice(at)}`;
  }, [email]);

  const validatePasswordRules = [
    { required: true, message: "Please enter new password!" },
    { min: 8, message: "Password must be at least 8 characters long!" },
    {
      pattern: /^[a-zA-Z0-9]+$/,
      message: "Password should be alphanumeric (A-Z, a-z, 0-9) only.",
    },
  ];

  const onFinish = async (values) => {
    if (!email || !otp) {
      ShowToast("Invalid reset link. Please request again.", Severty.ERROR);
      return;
    }

    setLoading(true);
    try {
      const otpVerified = await request({
        url: apiPath.authVerifyOtp,
        method: "POST",
        data: { email, otp },
        onError: (err) => {
          ShowToast(err?.response?.data?.message || "OTP verification failed", Severty.ERROR);
        },
      });

      if (!otpVerified) return;

      const resetRes = await request({
        url: apiPath.authResetPassword,
        method: "POST",
        data: { email, password: values.newPassword },
        onSuccess: (data) => {
          ShowToast(data?.message || "Password reset successful", Severty.SUCCESS);
        },
        onError: (err) => {
          ShowToast(err?.response?.data?.message || "Password reset failed", Severty.ERROR);
        },
      });

      if (!resetRes) return;

      form.resetFields();
      navigate("/login", {
        replace: true,
        state: { fromPasswordReset: true, resetEmail: email },
      });
    } finally {
      setLoading(false);
    }
  };

  const validLink = Boolean(email && otp);

  return (
    <Layout className="layout-default layout-signin">
      <Row className="align-items-center layout-signin-rowsss">
        <Col span={24}>
          <Content className="signin">
            <div className="signin-box">
              <div className="signup-logo" style={{ marginBottom: 8 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #e67e22 0%, #d35400 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin:"0 auto",
                  marginBottom:"10px"
                  }}
                >
                  <LockOutlined style={{ fontSize: 24, color: "#fff" }} />
                </div>
                <Title level={2} style={{ marginBottom: 4 }}>
                  Set new password
                </Title>
                <Text type="secondary" style={{ fontSize: 15 }}>
                  Eatstoats admin
                </Text>
              </div>

              {/* {!validLink ? (
                <Paragraph type="danger" style={{ marginBottom: 16 }}>
                  This reset link is invalid or expired. Request a new one from the login page.
                </Paragraph>
              ) : (
                <>
                  <Paragraph type="secondary" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
                    Choose a strong password for your account
                    {emailHint ? (
                      <>
                        {" "}
                        (<Text code>{emailHint}</Text>)
                      </>
                    ) : null}
                    . This page opened from your email reset link.
                  </Paragraph>
                </>
              )} */}

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ confirm_new_password: "" }}
                requiredMark={false}
              >
                <Form.Item label="New password" name="newPassword" rules={validatePasswordRules}>
                  <Input.Password
                    placeholder="At least 8 alphanumeric characters"
                    disabled={loading || !validLink}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm new password"
                  name="confirm_new_password"
                  dependencies={["newPassword"]}
                  hasFeedback
                  normalize={(value) => value?.trim()}
                  rules={[
                    { required: true, message: "Please confirm your password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match!"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Re-enter password"
                    disabled={loading || !validLink}
                    size="large"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={!validLink}
                    block
                    size="large"
                    style={{ height: 46, fontWeight: 600 }}
                  >
                    Update password
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: "center", marginTop: 8 }}>
                <Link to="/login" style={{ fontSize: 14, color: "inherit" }}>
                  <ArrowLeftOutlined style={{ marginRight: 6 }} />
                  Back to sign in
                </Link>
              </div>
            </div>
          </Content>
        </Col>
      </Row>
    </Layout>
  );
};

export default ResetPasswordFromLink;
